import { Deferred, router, usePage } from "@inertiajs/react";
import { Check, ChevronsUpDown, Search, User, Users, X } from "lucide-react";
import {
	type ComponentProps,
	Fragment,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import DateRangeFilter, {
	BACKWARD_PRESETS,
	BIDIRECTIONAL_PRESETS,
} from "@/components/admin-portal/appointment/date-range-filter";
import DotBadgeWithLabel from "@/components/shared/dot-badge";
import MultiSelectBadges, {
	type MultiSelectBadgesProps,
} from "@/components/shared/multi-select-badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import {
	type AppointmentFilters,
	countActiveFilters,
	EMPTY_FILTERS,
	FILTER_GROUPS,
	type FilterGroupKey,
} from "@/lib/appointments/filter-groups";
import { groupLocationsByCountry } from "@/lib/locations";
import { cn, debounce, populateQueryParams } from "@/lib/utils";
import type { AppointmentIndexGlobalPageProps } from "@/pages/AdminPortal/Appointment/Index";

// Each date filter is one control over two params, so both ends move together — applying
// them one at a time would fire two visits and briefly filter on a range nobody selected.
//
// They share a control but not a direction in time: a row can only have been created
// already, whereas a visit is normally booked ahead, so each carries the preset list that
// points the way its own dates run — the same split the Drafts sheet makes.
const DATE_RANGE_FILTERS = [
	{
		key: "visit",
		fromKey: "visitFrom",
		toKey: "visitTo",
		placeholderKey: "visit_range_placeholder",
		presets: BIDIRECTIONAL_PRESETS,
	},
	{
		key: "created",
		fromKey: "createdFrom",
		toKey: "createdTo",
		placeholderKey: "created_range_placeholder",
		presets: BACKWARD_PRESETS,
	},
] as const;

// The three yes/no filters differ only in which key they write, so they are one control
// rendered three times rather than three near-identical Selects. They do not sit in the
// same group: "all visits completed" describes the visit, the other two the paperwork.
const YES_NO_LABELS = {
	hasSoap: "has_soap.placeholder",
	hasEvidence: "has_evidence.placeholder",
	allVisitsCompleted: "all_visits_completed.placeholder",
} as const;

const VISIT_POSITIONS = ["first", "last"] as const;

export interface FilterListProps extends ComponentProps<"section"> {
	isShow: boolean;
	onToggleShow: () => void;
}

export default function FilterList({
	className,
	onToggleShow,
}: FilterListProps) {
	const { url: pageURL, props: globalProps } =
		usePage<AppointmentIndexGlobalPageProps>();
	const { t } = useTranslation("appointments");
	const { t: taf } = useTranslation("appointments", { keyPrefix: "filter" });

	const [isSearching, setIsSearching] = useState(false);
	const [filterBy, setFilterBy] = useState<AppointmentFilters>(() => {
		const currentQuery = globalProps?.adminPortal?.currentQuery as
			| Record<string, string>
			| undefined;

		// Read back off the query string, so a filtered view survives reload and can be
		// shared as a link.
		return Object.fromEntries(
			Object.keys(EMPTY_FILTERS).map((key) => [key, currentQuery?.[key] || ""]),
		) as AppointmentFilters;
	});
	const updateQueryParams = useCallback(
		debounce((url) => {
			router.get(
				url,
				{},
				{
					preserveState: true,
					only: ["adminPortal", "flash", "errors", "appointments"],
					onStart: () => {
						setIsSearching(true);
					},
					onFinish: () => {
						setTimeout(() => {
							setIsSearching(false);
						}, 250);
					},
				},
			);
		}, 250),
		[],
	);
	// Takes a patch rather than a single key because a date range moves both of its ends
	// together; applying them one at a time would filter on a range nobody selected.
	const handleFilterPatch = useCallback(
		(patch: Partial<AppointmentFilters>) => {
			const nextFilters = { ...filterBy, ...patch };
			setFilterBy(nextFilters);

			// Only send the changed field, updateQueryParams will merge and clean status if needed
			const { fullUrl } = populateQueryParams(
				pageURL,
				deepTransformKeysToSnakeCase({
					...nextFilters,
					// null value means reset the another param value
					page: null,
					limit: null,
				}),
			);
			updateQueryParams(fullUrl);
		},
		[filterBy, pageURL, updateQueryParams],
	);
	const handleFilterBy = useCallback(
		({ value, type }: { value: string; type: keyof AppointmentFilters }) =>
			handleFilterPatch({ [type]: value }),
		[handleFilterPatch],
	);
	// Adds or removes one entry of a comma-separated multi-select param.
	const handleToggleInCsv = useCallback(
		(type: keyof AppointmentFilters, entry: string) => {
			const current = (filterBy[type] || "").split(",").filter(Boolean);
			const next = current.includes(entry)
				? current.filter((item) => item !== entry)
				: [...current, entry];

			handleFilterPatch({ [type]: next.join(",") });
		},
		[filterBy, handleFilterPatch],
	);
	const onResetAll = useCallback(() => {
		setFilterBy(EMPTY_FILTERS);

		const { fullUrl } = populateQueryParams(
			pageURL,
			deepTransformKeysToSnakeCase({
				...EMPTY_FILTERS,
				// null value means reset the another param value
				page: null,
				limit: null,
			}),
		);
		updateQueryParams(fullUrl);
	}, [pageURL, updateQueryParams]);

	// * filter by option items
	const locations = useMemo(
		() => globalProps?.filterOptionsData?.locations || [],
		[globalProps?.filterOptionsData?.locations],
	);
	const groupedLocations = useMemo(
		() => groupLocationsByCountry(locations),
		[locations],
	);
	const assignedToList = useMemo(() => {
		return [
			{
				value: "me",
				label: taf("assigned_to.items.me").toUpperCase(),
				icon: (
					<User className="flex-shrink-0 text-muted-foreground/75 size-4" />
				),
			},
			{
				value: "anyone",
				label: taf("assigned_to.items.anyone").toUpperCase(),
				icon: (
					<Users className="flex-shrink-0 text-muted-foreground/75 size-4" />
				),
			},
		];
	}, [taf]);

	// * for filtering by patient genders
	// Memoized list of all available patient genders from global props
	const availableGenders = useMemo(
		() => globalProps?.filterOptionsData?.patientGenders || [],
		[globalProps?.filterOptionsData?.patientGenders],
	);
	// Extract selected genders as objects with value/label for the UI
	const selectedGenderOptions = useMemo(() => {
		const selectedValues = filterBy.patientGenders.split(",");
		return availableGenders
			.filter((gender) => selectedValues.includes(gender))
			.map((gender) => ({
				value: gender,
				label: gender,
			}));
	}, [availableGenders, filterBy.patientGenders]);
	// Raw selected genders as an array of strings
	const selectedGenderValues = useMemo(
		() => filterBy?.patientGenders?.split(",").map((g) => g.trim()),
		[filterBy.patientGenders],
	);
	// Handler to remove a gender badge from selection
	const handleRemoveGenderBadge = useCallback(
		(items: MultiSelectBadgesProps["items"]) => {
			const updatedValues = items.map((item) => item.value).join(",");
			handleFilterBy({
				type: "patientGenders",
				value: updatedValues,
			});
		},
		[handleFilterBy],
	);
	// Handler to remove a therapist-type badge from selection
	const handleRemoveTherapistTypeBadge = useCallback(
		(items: MultiSelectBadgesProps["items"]) => {
			handleFilterBy({
				type: "therapistEmploymentTypes",
				value: items.map((item) => item.value).join(","),
			});
		},
		[handleFilterBy],
	);
	// Handler to remove a visit-position badge from selection
	const handleRemoveVisitPositionBadge = useCallback(
		(items: MultiSelectBadgesProps["items"]) => {
			handleFilterBy({
				type: "visitPositions",
				value: items.map((item) => item.value).join(","),
			});
		},
		[handleFilterBy],
	);
	// Handler to select/unselect a gender
	const handleToggleGenderSelection = useCallback(
		(gender: string) => {
			const currentSelection = selectedGenderValues ?? [];
			const isSelected = currentSelection.includes(gender);

			const updatedSelection = isSelected
				? currentSelection.filter((g) => g !== gender)
				: [...currentSelection, gender];

			handleFilterBy({
				type: "patientGenders",
				value: updatedSelection.filter(Boolean).join(","),
			});
		},
		[selectedGenderValues, handleFilterBy],
	);

	// * for filtering by therapist employment type
	const therapistTypeOptions = useMemo(
		() => globalProps?.filterOptionsData?.therapistEmploymentTypes || [],
		[globalProps?.filterOptionsData?.therapistEmploymentTypes],
	);
	const selectedTherapistTypeValues = useMemo(
		() => filterBy.therapistEmploymentTypes.split(",").filter(Boolean),
		[filterBy.therapistEmploymentTypes],
	);
	const selectedTherapistTypeOptions = useMemo(
		() =>
			therapistTypeOptions.filter((type) =>
				selectedTherapistTypeValues.includes(type.value),
			),
		[therapistTypeOptions, selectedTherapistTypeValues],
	);

	// * for filtering by visit position
	const visitPositionOptions = useMemo(
		() =>
			VISIT_POSITIONS.map((position) => ({
				value: position,
				label: taf(`visit_position.items.${position}`),
			})),
		[taf],
	);
	const selectedVisitPositionValues = useMemo(
		() => filterBy.visitPositions.split(",").filter(Boolean),
		[filterBy.visitPositions],
	);
	const selectedVisitPositionOptions = useMemo(
		() =>
			visitPositionOptions.filter((position) =>
				selectedVisitPositionValues.includes(position.value),
			),
		[visitPositionOptions, selectedVisitPositionValues],
	);

	// * for filtering by services
	// Memoized list of available services with formatted labels
	const availableServices = useMemo(
		() =>
			globalProps?.filterOptionsData?.services?.map((service) => ({
				...service,
				label: service.name.replaceAll("_", " "),
			})) || [],
		[globalProps?.filterOptionsData?.services],
	);
	// Selected services as full objects (for UI badges)
	const selectedServiceOptions = useMemo(
		() =>
			availableServices
				.filter((service) => filterBy.serviceIds.includes(String(service.id)))
				.map((service) => ({ ...service, label: service.label })),
		[availableServices, filterBy.serviceIds],
	);
	// Raw selected service IDs as an array of strings
	const selectedServiceIds = useMemo(
		() => filterBy?.serviceIds?.split(",").map((id) => id.trim()),
		[filterBy.serviceIds],
	);
	// Handler to remove a service badge
	const handleRemoveServiceBadge = useCallback(
		(items: MultiSelectBadgesProps["items"]) => {
			const updatedIds = items.map((item) => String(item.id)).join(",");
			handleFilterBy({
				type: "serviceIds",
				value: updatedIds,
			});
		},
		[handleFilterBy],
	);
	// Handler to toggle service selection
	const handleToggleServiceSelection = useCallback(
		(id: string) => {
			const currentSelection = selectedServiceIds ?? [];
			const isSelected = currentSelection.includes(id);

			const updatedSelection = isSelected
				? currentSelection.filter((sid) => sid !== id)
				: [...currentSelection, id];

			handleFilterBy({
				type: "serviceIds",
				value: updatedSelection.filter(Boolean).join(","),
			});
		},
		[selectedServiceIds, handleFilterBy],
	);

	// *for filtering by packages
	// * Flat list of all available packages across all services
	const availablePackages = useMemo(() => {
		return (
			globalProps?.filterOptionsData?.services
				?.flatMap((service) =>
					(service.packages || []).map((pkg) => ({
						...pkg,
						label: pkg.name,
						serviceName: service.name,
					})),
				)
				?.filter(Boolean) || []
		);
	}, [globalProps?.filterOptionsData?.services]);

	// * Grouped package list, organized by service name
	const groupedPackageOptions = useMemo(
		() =>
			availableServices?.map((service) => ({
				header: service.name.replaceAll("_", " "),
				packages: service.packages || [],
			})),
		[availableServices],
	);

	// * Raw selected package IDs as an array
	const selectedPackageIds = useMemo(
		() =>
			filterBy?.packageIds
				? filterBy.packageIds.split(",").map((id) => id.trim())
				: [],
		[filterBy.packageIds],
	);

	// * Selected packages (for UI badges)
	const selectedPackageOptions = useMemo(() => {
		return availablePackages.filter((pkg) =>
			selectedPackageIds.includes(String(pkg.id)),
		);
	}, [availablePackages, selectedPackageIds]);

	// * Handler to remove a package badge
	const handleRemovePackageBadge = useCallback(
		(items: MultiSelectBadgesProps["items"]) => {
			const updatedIds = items.map((item) => String(item.id)).join(",");
			handleFilterBy({
				type: "packageIds",
				value: updatedIds,
			});
		},
		[handleFilterBy],
	);
	// Handler to toggle service selection
	const handleTogglePackageSelection = useCallback(
		(id: string) => {
			const currentSelection = selectedPackageIds ?? [];
			const isSelected = currentSelection.includes(id);

			const updatedSelection = isSelected
				? currentSelection.filter((sid) => sid !== id)
				: [...currentSelection, id];

			handleFilterBy({
				type: "packageIds",
				value: updatedSelection.filter(Boolean).join(","),
			});
		},
		[selectedPackageIds, handleFilterBy],
	);

	// * for filtering by statuses
	const apptStatuses = useMemo(() => {
		return [
			{
				label: t("tab.title.cancelled"),
				value: "cancel",
				color: "destructive" as const,
			},
			{
				// Outline rather than destructive: a refund is not a cancellation, and
				// the neutral group is where the badge's stone hue points.
				label: t("tab.title.refunded"),
				value: "refunded",
				color: "outline" as const,
			},
			{
				label: t("tab.title.unschedule"),
				value: "unschedule",
				color: "outline" as const,
			},
			{
				label: t("tab.title.on_hold"),
				value: "on_hold",
				color: "outline" as const,
			},
			{
				label: t("tab.title.pending_payment"),
				value: "pending_payment",
				color: "warning" as const,
			},
			{
				label: t("tab.title.pending_patient_approval"),
				value: "pending_patient_approval",
				color: "warning" as const,
			},
			{
				label: t("tab.title.tentative"),
				value: "tentative",
				color: "warning" as const,
			},
			{
				label: t("tab.title.pending_therapist"),
				value: "pending_therapist",
				color: "warning" as const,
			},
			{
				label: t("tab.title.pending_mitra_approval"),
				value: "pending_mitra_approval",
				color: "warning" as const,
			},
			{
				label: t("tab.title.scheduled"),
				value: "scheduled",
				color: "blue" as const,
			},
			{
				label: t("tab.title.past"),
				value: "past",
				color: "success" as const,
			},
			{
				label: t("tab.title.upcoming"),
				value: "upcoming",
				color: "success" as const,
			},
			{
				label: t("tab.title.completed"),
				value: "completed",
				color: "success" as const,
			},
		];
	}, [t]);
	const selectedStatus = useMemo(() => {
		return apptStatuses?.find((s) => s.value === filterBy?.status);
	}, [apptStatuses, filterBy?.status]);

	const renderYesNoFilter = (key: keyof typeof YES_NO_LABELS) => (
		<div key={key} className="col-span-full md:col-span-1">
			<Select
				value={filterBy[key]}
				onValueChange={(value) => handleFilterBy({ type: key, value })}
			>
				<SelectTrigger
					className={cn(
						"bg-input shadow-inner",
						!filterBy[key] ? "text-muted-foreground" : "",
					)}
				>
					<SelectValue placeholder={`${taf(YES_NO_LABELS[key])}...`} />
				</SelectTrigger>
				<SelectContent>
					<SelectGroup>
						<SelectItem value="yes">{taf("yes_no.yes")}</SelectItem>
						<SelectItem value="no">{taf("yes_no.no")}</SelectItem>
					</SelectGroup>

					<SelectSeparator />

					<SelectGroup>
						<Button
							className="w-full px-2 font-medium uppercase"
							variant="ghost"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								handleFilterBy({ type: key, value: "" });
							}}
						>
							{taf("yes_no.clear")}
						</Button>
					</SelectGroup>
				</SelectContent>
			</Select>
		</div>
	);

	const identityFields = (
		<>
			<div className="col-span-full md:col-span-1">
				<Input
					type="text"
					className="shadow-inner bg-input"
					placeholder={`${taf("reg_number.placeholder")}...`}
					value={filterBy.registrationNumber}
					StartIcon={{ icon: Search }}
					isLoading={isSearching}
					EndIcon={
						filterBy.registrationNumber
							? {
									icon: X,
									isButton: true,
									handleOnClick: () => {
										handleFilterBy({ type: "registrationNumber", value: "" });
									},
								}
							: undefined
					}
					onChange={(event) => {
						event.preventDefault();
						handleFilterBy({
							type: "registrationNumber",
							value: event.target.value,
						});
					}}
				/>
			</div>

			<div className="col-span-full md:col-span-1">
				<Input
					type="text"
					className="shadow-inner bg-input"
					placeholder={`${taf("invoice_number.placeholder")}...`}
					value={filterBy.invoiceNumber}
					StartIcon={{ icon: Search }}
					isLoading={isSearching}
					EndIcon={
						filterBy.invoiceNumber
							? {
									icon: X,
									isButton: true,
									handleOnClick: () => {
										handleFilterBy({ type: "invoiceNumber", value: "" });
									},
								}
							: undefined
					}
					onChange={(event) => {
						event.preventDefault();
						handleFilterBy({
							type: "invoiceNumber",
							value: event.target.value,
						});
					}}
				/>
			</div>

			<div className="col-span-full md:col-span-1">
				<Input
					type="text"
					className="shadow-inner bg-input"
					placeholder={`${taf("draft_id.placeholder")}...`}
					value={filterBy.draftId}
					StartIcon={{ icon: Search }}
					isLoading={isSearching}
					EndIcon={
						filterBy.draftId
							? {
									icon: X,
									isButton: true,
									handleOnClick: () => {
										handleFilterBy({ type: "draftId", value: "" });
									},
								}
							: undefined
					}
					onChange={(event) => {
						event.preventDefault();
						handleFilterBy({ type: "draftId", value: event.target.value });
					}}
				/>
			</div>
		</>
	);

	const visitFields = (
		<>
			<div className="col-span-full md:col-span-1">
				<Popover>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							className={cn(
								"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
								!filterBy?.status && "text-muted-foreground",
							)}
						>
							<span className="truncate">
								{selectedStatus ? (
									<DotBadgeWithLabel
										className="relative flex-shrink-0 text-left"
										variant={selectedStatus.color}
									>
										<span
											title={selectedStatus.label}
											className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
										>
											{selectedStatus.label}
										</span>
									</DotBadgeWithLabel>
								) : (
									<span>{`${taf("status.placeholder")}...`}</span>
								)}
							</span>

							<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="p-0 w-fit" align="start" side="bottom">
						<Command>
							<CommandInput
								placeholder={`${taf("status.search.placeholder")}...`}
								className="h-9"
							/>
							<CommandList>
								<CommandEmpty>{taf("status.search.empty")}</CommandEmpty>
								<CommandGroup>
									{apptStatuses?.length ? (
										apptStatuses?.map((s) => (
											<CommandItem
												key={s.value}
												value={s.value}
												onSelect={() =>
													handleFilterBy({ type: "status", value: s.value })
												}
											>
												<DotBadgeWithLabel
													className="relative flex-shrink-0 text-left"
													variant={s.color}
												>
													<span
														title={s.label}
														className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
													>
														{s.label}
													</span>
												</DotBadgeWithLabel>

												<Check
													className={cn(
														"ml-auto",
														s.value === filterBy?.status
															? "opacity-100"
															: "opacity-0",
													)}
												/>
											</CommandItem>
										))
									) : (
										<CommandItem value="" disabled>
											<span>{taf("status.search.empty")}</span>
										</CommandItem>
									)}
								</CommandGroup>
							</CommandList>

							<CommandList>
								<CommandGroup>
									<CommandSeparator className="mb-2" />
									<CommandItem
										onSelect={() => {
											handleFilterBy({ type: "status", value: "" });
										}}
									>
										<span className="mx-auto font-medium text-center uppercase w-fit">
											{taf("status.search.clear")}
										</span>
									</CommandItem>
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>
			</div>

			{DATE_RANGE_FILTERS.map((range) => (
				<div key={range.key} className="grid col-span-full md:col-span-1">
					<DateRangeFilter
						value={{
							from: filterBy[range.fromKey],
							to: filterBy[range.toKey],
						}}
						onChange={(next) =>
							handleFilterPatch({
								[range.fromKey]: next.from,
								[range.toKey]: next.to,
							})
						}
						placeholder={`${taf(range.placeholderKey)}...`}
						presets={range.presets}
					/>
				</div>
			))}

			<div className="col-span-full md:col-span-1">
				<Popover>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							className={cn(
								"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
								!selectedVisitPositionOptions?.length &&
									"text-muted-foreground",
							)}
						>
							<MultiSelectBadges
								placeholder={`${taf("visit_position.placeholder")}...`}
								maxShows={1}
								items={selectedVisitPositionOptions}
								onRemoveRestAll={(values) =>
									handleRemoveVisitPositionBadge(values)
								}
								onRemoveSingle={(values) =>
									handleRemoveVisitPositionBadge(values)
								}
							/>

							<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="p-0 w-fit" align="start" side="bottom">
						<Command>
							<CommandInput
								placeholder={`${taf("visit_position.search.placeholder")}...`}
								className="h-9"
							/>
							<CommandList>
								<CommandEmpty>
									{taf("visit_position.search.empty")}
								</CommandEmpty>
								<CommandGroup>
									{visitPositionOptions.map((position) => (
										<CommandItem
											key={position.value}
											value={position.value}
											onSelect={() =>
												handleToggleInCsv("visitPositions", position.value)
											}
										>
											<span
												title={position.label}
												className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
											>
												{position.label}
											</span>

											<Check
												className={cn(
													"ml-auto",
													selectedVisitPositionValues.includes(position.value)
														? "opacity-100"
														: "opacity-0",
												)}
											/>
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>

							<CommandList>
								<CommandGroup>
									<CommandSeparator className="mb-2" />
									<CommandItem
										onSelect={() => {
											handleFilterBy({ type: "visitPositions", value: "" });
										}}
									>
										<span className="mx-auto font-medium text-center uppercase w-fit">
											{taf("visit_position.search.clear")}
										</span>
									</CommandItem>
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>
			</div>

			{renderYesNoFilter("allVisitsCompleted")}
		</>
	);

	const patientFields = (
		<>
			<div className="col-span-full md:col-span-1">
				<Input
					type="text"
					className="shadow-inner bg-input"
					placeholder={`${taf("patient_name.placeholder")}...`}
					value={filterBy.patient}
					StartIcon={{ icon: Search }}
					isLoading={isSearching}
					EndIcon={
						filterBy.patient
							? {
									icon: X,
									isButton: true,
									handleOnClick: () => {
										handleFilterBy({ type: "patient", value: "" });
									},
								}
							: undefined
					}
					onChange={(event) => {
						event.preventDefault();
						handleFilterBy({ type: "patient", value: event.target.value });
					}}
				/>
			</div>

			<Deferred
				data={["filterOptionsData"]}
				fallback={
					<Skeleton className="w-full rounded-md col-span-full md:col-span-1 h-9" />
				}
			>
				<div className="col-span-full md:col-span-1">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
									!selectedGenderOptions?.length && "text-muted-foreground",
								)}
							>
								<MultiSelectBadges
									placeholder={`${taf("patient_genders.placeholder")}...`}
									maxShows={1}
									items={selectedGenderOptions}
									onRemoveRestAll={(values) => handleRemoveGenderBadge(values)}
									onRemoveSingle={(values) => handleRemoveGenderBadge(values)}
								/>

								<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="p-0 w-fit" align="start" side="bottom">
							<Command>
								<CommandInput
									placeholder={`${taf("patient_genders.search.placeholder")}...`}
									className="h-9"
								/>
								<CommandList>
									<CommandEmpty>
										{taf("patient_genders.search.empty")}
									</CommandEmpty>
									<CommandGroup>
										{availableGenders?.length ? (
											availableGenders?.map((gender) => (
												<CommandItem
													key={gender}
													value={gender}
													onSelect={() => {
														handleToggleGenderSelection(gender);
													}}
												>
													<span
														title={gender}
														className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
													>
														{gender}
													</span>

													<Check
														className={cn(
															"ml-auto",
															selectedGenderOptions.some(
																(pg) => pg.value === gender,
															)
																? "opacity-100"
																: "opacity-0",
														)}
													/>
												</CommandItem>
											))
										) : (
											<CommandItem value="" disabled>
												<span>{taf("patient_genders.search.empty")}</span>
											</CommandItem>
										)}
									</CommandGroup>
								</CommandList>

								<CommandList>
									<CommandGroup>
										<CommandSeparator className="mb-2" />
										<CommandItem
											onSelect={() => {
												handleFilterBy({ type: "patientGenders", value: "" });
											}}
										>
											<span className="mx-auto font-medium text-center uppercase w-fit">
												{taf("patient_genders.search.clear")}
											</span>
										</CommandItem>
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				</div>
			</Deferred>
		</>
	);

	const therapistFields = (
		<>
			<div className="col-span-full md:col-span-1">
				<Input
					type="text"
					className="shadow-inner bg-input"
					placeholder={`${taf("therapist_name.placeholder")}...`}
					value={filterBy.therapist}
					StartIcon={{ icon: Search }}
					isLoading={isSearching}
					EndIcon={
						filterBy.therapist
							? {
									icon: X,
									isButton: true,
									handleOnClick: () => {
										handleFilterBy({ type: "therapist", value: "" });
									},
								}
							: undefined
					}
					onChange={(event) => {
						event.preventDefault();
						handleFilterBy({ type: "therapist", value: event.target.value });
					}}
				/>
			</div>

			<Deferred
				data={["filterOptionsData"]}
				fallback={
					<Skeleton className="w-full rounded-md col-span-full md:col-span-1 h-9" />
				}
			>
				<div className="col-span-full md:col-span-1">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
									!selectedTherapistTypeOptions?.length &&
										"text-muted-foreground",
								)}
							>
								<MultiSelectBadges
									placeholder={`${taf("therapist_type.placeholder")}...`}
									maxShows={1}
									items={selectedTherapistTypeOptions}
									onRemoveRestAll={(values) =>
										handleRemoveTherapistTypeBadge(values)
									}
									onRemoveSingle={(values) =>
										handleRemoveTherapistTypeBadge(values)
									}
								/>

								<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="p-0 w-fit" align="start" side="bottom">
							<Command>
								<CommandInput
									placeholder={`${taf("therapist_type.search.placeholder")}...`}
									className="h-9"
								/>
								<CommandList>
									<CommandEmpty>
										{taf("therapist_type.search.empty")}
									</CommandEmpty>
									<CommandGroup>
										{therapistTypeOptions?.length ? (
											therapistTypeOptions.map((type) => (
												<CommandItem
													key={type.value}
													value={type.value}
													onSelect={() =>
														handleToggleInCsv(
															"therapistEmploymentTypes",
															type.value,
														)
													}
												>
													<span
														title={type.label}
														className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
													>
														{type.label}
													</span>

													<Check
														className={cn(
															"ml-auto",
															selectedTherapistTypeValues.includes(type.value)
																? "opacity-100"
																: "opacity-0",
														)}
													/>
												</CommandItem>
											))
										) : (
											<CommandItem value="" disabled>
												<span>{taf("therapist_type.search.empty")}</span>
											</CommandItem>
										)}
									</CommandGroup>
								</CommandList>

								<CommandList>
									<CommandGroup>
										<CommandSeparator className="mb-2" />
										<CommandItem
											onSelect={() => {
												handleFilterBy({
													type: "therapistEmploymentTypes",
													value: "",
												});
											}}
										>
											<span className="mx-auto font-medium text-center uppercase w-fit">
												{taf("therapist_type.search.clear")}
											</span>
										</CommandItem>
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				</div>
			</Deferred>

			<div className="col-span-full md:col-span-1">
				<Select
					value={filterBy.assignedTo}
					onValueChange={(value) =>
						handleFilterBy({ type: "assignedTo", value })
					}
				>
					<SelectTrigger
						className={cn(
							"bg-input shadow-inner",
							!filterBy.assignedTo ? "text-muted-foreground" : "",
						)}
					>
						<SelectValue placeholder={`${taf("assigned_to.placeholder")}...`} />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{assignedToList.map((i) => (
								<SelectItem key={i.value} value={i.value}>
									<div className="flex flex-row items-center gap-2 uppercase">
										{i.icon}
										{i.label}
									</div>
								</SelectItem>
							))}
						</SelectGroup>

						<SelectSeparator />

						<SelectGroup>
							<Button
								className="w-full px-2 font-medium uppercase"
								variant="ghost"
								size="sm"
								onClick={(e) => {
									e.stopPropagation();
									handleFilterBy({ type: "assignedTo", value: "" });
								}}
							>
								{taf("assigned_to.search.clear")}
							</Button>
						</SelectGroup>
					</SelectContent>
				</Select>
			</div>
		</>
	);

	const serviceRegionFields = (
		<>
			<Deferred
				data={["filterOptionsData"]}
				fallback={
					<Skeleton className="w-full rounded-md col-span-full md:col-span-1 h-9" />
				}
			>
				<div className="col-span-full md:col-span-1">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
									!selectedServiceOptions?.length && "text-muted-foreground",
								)}
							>
								<MultiSelectBadges
									placeholder={`${taf("services.placeholder")}...`}
									maxShows={1}
									items={selectedServiceOptions}
									onRemoveRestAll={(values) => handleRemoveServiceBadge(values)}
									onRemoveSingle={(values) => handleRemoveServiceBadge(values)}
								/>

								<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="p-0 w-fit" align="start" side="bottom">
							<Command>
								<CommandInput
									placeholder={`${taf("services.search.placeholder")}...`}
									className="h-9"
								/>
								<CommandList>
									<CommandEmpty>{taf("services.search.empty")}</CommandEmpty>
									<CommandGroup>
										{availableServices?.length ? (
											availableServices?.map((service) => (
												<CommandItem
													key={service.id}
													value={String(service.id)}
													onSelect={() => {
														const id = String(service.id);
														handleToggleServiceSelection(id);
													}}
												>
													<span
														title={service.label}
														className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
													>
														{service.label}
													</span>

													<Check
														className={cn(
															"ml-auto",
															filterBy?.serviceIds.includes(String(service.id))
																? "opacity-100"
																: "opacity-0",
														)}
													/>
												</CommandItem>
											))
										) : (
											<CommandItem value="" disabled>
												<span>{taf("services.search.empty")}</span>
											</CommandItem>
										)}
									</CommandGroup>
								</CommandList>

								<CommandList>
									<CommandGroup>
										<CommandSeparator className="mb-2" />
										<CommandItem
											onSelect={() => {
												handleFilterBy({ type: "serviceIds", value: "" });
											}}
										>
											<span className="mx-auto font-medium text-center uppercase w-fit">
												{taf("services.search.clear")}
											</span>
										</CommandItem>
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				</div>
			</Deferred>

			<Deferred
				data={["filterOptionsData"]}
				fallback={
					<Skeleton className="w-full rounded-md col-span-full md:col-span-1 h-9" />
				}
			>
				<div className="col-span-full md:col-span-1">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
									!selectedPackageOptions?.length && "text-muted-foreground",
								)}
							>
								<MultiSelectBadges
									placeholder={`${taf("packages.placeholder")}...`}
									maxShows={1}
									items={selectedPackageOptions}
									onRemoveRestAll={(values) => handleRemovePackageBadge(values)}
									onRemoveSingle={(values) => handleRemovePackageBadge(values)}
								/>

								<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="p-0 w-fit" align="start" side="bottom">
							<Command>
								<CommandInput
									placeholder={`${taf("packages.search.placeholder")}...`}
									className="h-9"
								/>
								<CommandList>
									<CommandEmpty>{taf("packages.search.empty")}</CommandEmpty>
									<CommandGroup>
										{groupedPackageOptions?.length ? (
											groupedPackageOptions?.map((service) => (
												<CommandGroup
													key={service.header}
													heading={service.header}
												>
													{service?.packages?.length ? (
														service.packages.map((pkg) => (
															<CommandItem
																key={pkg.id}
																value={String(pkg.id)}
																onSelect={() => {
																	handleTogglePackageSelection(String(pkg.id));
																}}
															>
																<span
																	title={pkg.name}
																	className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
																>
																	{pkg.name}
																</span>
																<Check
																	className={cn(
																		"ml-auto",
																		selectedPackageIds?.some(
																			(p) => p === String(pkg.id),
																		)
																			? "opacity-100"
																			: "opacity-0",
																	)}
																/>
															</CommandItem>
														))
													) : (
														<CommandItem value={""} disabled>
															<span className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap">
																{taf("packages.search.empty")}
															</span>
														</CommandItem>
													)}
												</CommandGroup>
											))
										) : (
											<CommandItem value="" disabled>
												<span>{taf("packages.search.empty")}</span>
											</CommandItem>
										)}
									</CommandGroup>
								</CommandList>

								<CommandList>
									<CommandGroup>
										<CommandSeparator className="mb-2" />
										<CommandItem
											onSelect={() => {
												handleFilterBy({ type: "packageIds", value: "" });
											}}
										>
											<span className="mx-auto font-medium text-center uppercase w-fit">
												{taf("packages.search.clear")}
											</span>
										</CommandItem>
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				</div>
			</Deferred>

			<Deferred
				data={["filterOptionsData"]}
				fallback={
					<Skeleton className="w-full rounded-md col-span-full md:col-span-1 h-9" />
				}
			>
				<div className="col-span-full md:col-span-1">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
									!filterBy?.city && "text-muted-foreground",
								)}
							>
								<p className="truncate">
									{filterBy?.city
										? locations?.find(
												(location) => location.city === filterBy?.city,
											)?.city
										: `${taf("region.placeholder")}...`}
								</p>
								<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="p-0 w-fit" align="start" side="bottom">
							<Command>
								<CommandInput
									placeholder={`${taf("region.search.placeholder")}...`}
									className="h-9"
									autoComplete="address-level2"
								/>
								<CommandList>
									<CommandEmpty>{taf("region.search.empty")}</CommandEmpty>
									{groupedLocations?.map((location) => (
										<Fragment key={location.country}>
											<span className="block px-2 py-2 text-xs font-bold text-primary-foreground bg-primary">
												{location.country}
											</span>

											{location.states.map((state, stateIndex) => (
												<CommandGroup key={state.name} heading={state.name}>
													{state.cities.map((city) => (
														<CommandItem
															key={city.name}
															value={city.name}
															onSelect={() =>
																handleFilterBy({
																	type: "city",
																	value: city.name,
																})
															}
														>
															<span>{city.name}</span>
															<Check
																className={cn(
																	"ml-auto",
																	city.name === filterBy?.city
																		? "opacity-100"
																		: "opacity-0",
																)}
															/>
														</CommandItem>
													))}
													{location.states.length !== stateIndex + 1 && (
														<CommandSeparator className="mt-2" />
													)}
												</CommandGroup>
											))}
										</Fragment>
									))}
								</CommandList>

								<CommandList>
									<CommandGroup>
										<CommandSeparator className="mb-2" />
										<CommandItem
											onSelect={() => {
												handleFilterBy({ type: "city", value: "" });
											}}
										>
											<span className="mx-auto font-medium text-center uppercase w-fit">
												{taf("region.search.clear")}
											</span>
										</CommandItem>
									</CommandGroup>
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
				</div>
			</Deferred>
		</>
	);

	const groupFields: Record<FilterGroupKey, ReactNode> = {
		identity: identityFields,
		visit: visitFields,
		patient: patientFields,
		therapist: therapistFields,
		service_region: serviceRegionFields,
		documents: (
			<>
				{renderYesNoFilter("hasSoap")}
				{renderYesNoFilter("hasEvidence")}
			</>
		),
	};

	return (
		<section className={cn("w-full transition-all shadow-inner", className)}>
			{FILTER_GROUPS.map((group) => {
				const activeCount = countActiveFilters(group, filterBy);

				return (
					<div key={group.key} className="border-b last:border-b-0">
						<h3 className="py-3 text-xs font-semibold tracking-wide uppercase">
							{taf(`groups.${group.key}`)}
							{activeCount ? ` (${activeCount})` : ""}
						</h3>

						<div className="grid grid-cols-1 gap-4 pt-1 pb-4 md:grid-cols-2 md:gap-2 lg:grid-cols-3">
							{groupFields[group.key]}
						</div>
					</div>
				);
			})}

			<div className="flex flex-col justify-end gap-4 mt-4 md:gap-2 md:flex-row">
				<Button
					type="button"
					size="sm"
					variant="ghost"
					className="w-full md:w-fit"
					onClick={(event) => {
						event.preventDefault();

						onToggleShow();
					}}
				>
					{t("button.close_filter")}
				</Button>

				<Button
					type="button"
					size="sm"
					variant="ghost-destructive"
					className="w-full md:w-fit"
					onClick={(event) => {
						event.preventDefault();

						onResetAll();
					}}
				>
					{t("button.clear_all_filters")}
				</Button>
			</div>
		</section>
	);
}
